import type { Breadcrumb } from './ontology/types';

/** Maximum number of breadcrumbs retained; older entries are dropped (ring buffer size). */
const BREADCRUMB_CAPACITY = 40;

/** Internal source of truth for the captured breadcrumbs. */
let breadcrumbs: Breadcrumb[] = [];

/** Monotonic counter used to assign stable client-local breadcrumb ids. */
let nextBreadcrumbId = 1;

/**
 * Cached, externally exposed snapshot of {@link breadcrumbs}. The store refreshes this
 * copy only when the contents change so imperative readers see a stable array between
 * mutations, and the defensive copy shields the internal buffer from external mutation.
 */
let breadcrumbSnapshot: Breadcrumb[] = [];

/**
 * Appends a breadcrumb (stamping it with a stable id and the current time), trims the
 * buffer to {@link BREADCRUMB_CAPACITY}, and refreshes the cached snapshot exposed to
 * imperative readers.
 */
export function pushBreadcrumb(input: Omit<Breadcrumb, 'ts' | 'id'>): string {
  const nextBreadcrumb: Breadcrumb = {
    // Spread caller input first, then stamp the store-managed fields last so a
    // caller can never override the assigned `id`/`ts` (even by bypassing the type).
    ...input,
    id: String(nextBreadcrumbId),
    ts: Date.now(),
  };

  nextBreadcrumbId += 1;

  breadcrumbs = [...breadcrumbs, nextBreadcrumb].slice(-BREADCRUMB_CAPACITY);
  breadcrumbSnapshot = [...breadcrumbs];

  return nextBreadcrumb.id;
}

/**
 * Updates a retained breadcrumb by id and refreshes the cached snapshot returned to
 * imperative readers.
 * Missing ids are ignored so callers can safely patch entries that may already have been evicted.
 */
export function updateBreadcrumb(
  id: string,
  patch: Partial<Pick<Breadcrumb, 'outcome' | 'detail' | 'label'>>
): void {
  const breadcrumbIndex = breadcrumbs.findIndex((breadcrumb) => breadcrumb.id === id);
  if (breadcrumbIndex === -1) {
    return;
  }

  const currentBreadcrumb = breadcrumbs[breadcrumbIndex];
  const nextBreadcrumb: Breadcrumb = {
    ...currentBreadcrumb,
    ...patch,
    detail:
      patch.detail === undefined
        ? currentBreadcrumb.detail
        : {
            ...(currentBreadcrumb.detail ?? {}),
            ...patch.detail,
          },
  };

  breadcrumbs = breadcrumbs.map((breadcrumb, index) => {
    return index === breadcrumbIndex ? nextBreadcrumb : breadcrumb;
  });
  breadcrumbSnapshot = [...breadcrumbs];
}

/**
 * Returns the cached breadcrumb snapshot captured by the store. The reference stays
 * stable between mutations so open-time readers can snapshot once and hold that exact
 * breadcrumb trail until the next store update.
 */
export function getBreadcrumbSnapshot(): Breadcrumb[] {
  return breadcrumbSnapshot;
}

/** Clears the breadcrumb store; intended for resetting state between tests. */
export function resetBreadcrumbStoreForTest(): void {
  breadcrumbs = [];
  breadcrumbSnapshot = [];
  nextBreadcrumbId = 1;
}
